import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => `€ ${Number(v || 0).toLocaleString("de-AT", { minimumFractionDigits: 2 })}`;
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const fmtDT = (iso) => (iso ? `${fmtD(iso)}, ${new Date(iso).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" })}` : "—");

const STATUS = {
  paid: ["Bezahlt", { borderColor: "#16A34A", color: "#166534", background: "#F0FDF4" }],
  sent: ["Offen", { borderColor: "#C5A059", color: "#92650B", background: "#FFFBEB" }],
  overdue: ["Überfällig", { borderColor: "#DC2626", color: "#991B1B", background: "#FEF2F2" }],
  cancelled: ["Storniert", { borderColor: "#CBD5E1", color: "#475569", background: "#F8FAFC" }],
};

// Kanzlei-Portal: Honorarnoten — Versand, Zahlungsziel, Bezahldatum (keine separate Mail an den StB).
// Mit `clientId` zeigt das Panel nur die Noten EINES Mandanten (Detailansicht).
export const LexHonorarnotenPanel = ({ clientId = null }) => {
  const [notes, setNotes] = useState([]);
  const [clientFilter, setClientFilter] = useState("all");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/lex/advisor/honorarnoten`)
      .then((res) => setNotes(res.data.notes || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const downloadPdf = async (n) => {
    try {
      const res = await axios.get(`${API}/advisor/clients/${n.client_id}/invoice-pdf/${n.invoice_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${n.invoice_number || "honorarnote"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF konnte nicht geladen werden.");
    }
  };

  const clients = [...new Map(notes.map((n) => [n.client_id, n.client_name])).entries()];
  const shown = notes.filter((n) =>
    clientId ? n.client_id === clientId : (clientFilter === "all" || n.client_id === clientFilter));

  if (!loaded) return null;

  return (
    <div className="lex-card p-6 mt-8" data-testid="lex-honorarnoten-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4" style={{ color: "#C5A059" }} />
          <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-500">Honorarnoten</h2>
          <span className="lex-mono text-xs font-semibold px-2 py-0.5" style={{ background: "#0A1128", color: "#C5A059" }}
            data-testid="lex-honorarnoten-count">{shown.length}</span>
        </div>
        {!clientId && clients.length > 1 && (
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
            className="border border-slate-200 px-3 py-1.5 text-xs bg-white"
            data-testid="lex-honorarnoten-filter">
            <option value="all">Alle Mandanten</option>
            {clients.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="lex-honorarnoten-empty">Noch keine Honorarnoten.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                {!clientId && <th className="py-2 pr-3 font-semibold">Mandant</th>}
                <th className="py-2 pr-3 font-semibold">Note</th>
                <th className="py-2 pr-3 font-semibold text-right">Betrag</th>
                <th className="py-2 pr-3 font-semibold">Versendet am</th>
                <th className="py-2 pr-3 font-semibold">Zahlbar bis</th>
                <th className="py-2 pr-3 font-semibold">Bezahlt am</th>
                <th className="py-2 pr-3 font-semibold">Status</th>
                <th className="py-2 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {shown.map((n) => {
                const [label, style] = STATUS[n.status] || ["Entwurf", STATUS.cancelled[1]];
                return (
                  <tr key={n.invoice_id} className="border-b border-slate-50" data-testid={`lex-honorarnote-row-${n.invoice_id}`}>
                    {!clientId && <td className="py-2.5 pr-3 text-slate-700">{n.client_name}{n.lex_extra ? <span className="text-[10px] text-slate-400 ml-1">(außerordentlich)</span> : null}</td>}
                    <td className="py-2.5 pr-3 lex-mono text-slate-600">
                      {n.invoice_number || "—"}
                      {n.invoice_type === "storno" && <span className="text-[10px] text-slate-400 ml-1" data-testid={`lex-honorarnote-type-${n.invoice_id}`}>(Storno)</span>}
                      {n.invoice_type === "correction" && <span className="text-[10px] text-slate-400 ml-1" data-testid={`lex-honorarnote-type-${n.invoice_id}`}>(Korrektur)</span>}
                    </td>
                    <td className="py-2.5 pr-3 lex-mono text-right text-slate-800">{fmtEur(n.total)}</td>
                    <td className="py-2.5 pr-3 lex-mono text-slate-600" data-testid={`lex-honorarnote-sent-${n.invoice_id}`}>{fmtDT(n.sent_at)}</td>
                    <td className="py-2.5 pr-3 lex-mono text-slate-600">{fmtD(n.due_date)}</td>
                    <td className="py-2.5 pr-3 lex-mono text-slate-600" data-testid={`lex-honorarnote-paid-${n.invoice_id}`}>{fmtD(n.paid_at)}</td>
                    <td className="py-2.5 pr-3">
                      <span className="lex-mono text-[11px] px-2 py-0.5 border" style={style}>{label}</span>
                    </td>
                    <td className="py-2.5 text-right">
                      <button type="button" onClick={() => downloadPdf(n)}
                        className="text-slate-400 hover:text-slate-700 transition-colors" title="PDF herunterladen"
                        data-testid={`lex-honorarnote-pdf-${n.invoice_id}`}>
                        <Download className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-400 mt-3">
        Der Versand an die Kanzlei des Anwalts erfolgt automatisch per E-Mail — hier siehst du jede Note mit Versandzeitpunkt, Zahlungsziel und Bezahldatum.
      </p>
    </div>
  );
};

export default LexHonorarnotenPanel;
